/**
 * Vérif selfie plein écran (bloquant) — déclenchée uniquement quand l'admin
 * appuie sur « Vérif ». Intro verif.png → caméra frontale → shutter → confirm → chat.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

const AccessSelfieModal = ({ open, onConfirmFile, onBusyChange }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [phase, setPhase] = useState('intro'); // intro | camera | review | verifying
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [blobFile, setBlobFile] = useState(null);
  const [flash, setFlash] = useState(false);
  const [shutterPressed, setShutterPressed] = useState(false);
  const [camReady, setCamReady] = useState(false);
  const [camNonce, setCamNonce] = useState(0);

  const stopCamera = useCallback(() => {
    try {
      (streamRef.current?.getTracks?.() || []).forEach((t) => t.stop());
    } catch { /* */ }
    streamRef.current = null;
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch { /* */ }
    }
    setCamReady(false);
  }, []);

  // Attacher le stream UNE FOIS le <video> monté (sinon écran noir)
  useEffect(() => {
    if (!open || phase !== 'camera') return undefined;
    let cancelled = false;

    const attach = async () => {
      setError('');
      setCamReady(false);
      try {
        if (!streamRef.current || streamRef.current.getTracks().every((t) => t.readyState === 'ended')) {
          stopCamera();
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
              facingMode: 'user',
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          });
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          streamRef.current = stream;
        }

        const video = videoRef.current;
        if (!video || cancelled) return;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.srcObject = streamRef.current;

        const onReady = () => setCamReady(true);
        video.onloadedmetadata = async () => {
          try {
            await video.play();
          } catch { /* */ }
          if (!cancelled) onReady();
        };
        // Fallback si metadata déjà dispo
        if (video.readyState >= 1) {
          try { await video.play(); } catch { /* */ }
          if (!cancelled) onReady();
        }
      } catch {
        if (!cancelled) setError('Caméra indisponible. Autorise l\'accès photo et réessaie.');
      }
    };

    // Petit délai pour laisser AnimatePresence monter le nœud video
    const t = setTimeout(attach, 80);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, phase, camNonce, stopCamera]);

  useEffect(() => {
    if (!open) {
      stopCamera();
      setPhase('intro');
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setBlobFile(null);
      setError('');
      setFlash(false);
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      stopCamera();
    };
  }, [open, stopCamera]);

  useEffect(() => {
    if (!open) return undefined;
    const block = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', block, true);
    return () => window.removeEventListener('keydown', block, true);
  }, [open]);

  const goCamera = () => {
    setError('');
    setPhase('camera');
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setError('Caméra pas encore prête. Patiente une seconde.');
      return;
    }
    setShutterPressed(true);
    setFlash(true);
    setTimeout(() => setFlash(false), 180);
    setTimeout(() => setShutterPressed(false), 160);

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `selfie_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      setBlobFile(file);
      stopCamera();
      setPhase('review');
    }, 'image/jpeg', 0.92);
  };

  const confirm = async () => {
    if (!blobFile || typeof onConfirmFile !== 'function') return;
    setPhase('verifying');
    if (typeof onBusyChange === 'function') onBusyChange(true);
    try {
      await onConfirmFile(blobFile);
    } catch {
      setError('Une erreur est survenue. Réessaie plus tard.');
      setPhase('review');
    } finally {
      if (typeof onBusyChange === 'function') onBusyChange(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Vérification d'identité"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'calc(var(--gs-z-chat, 4500) + 50)',
        background: 'radial-gradient(ellipse 120% 80% at 50% -10%, rgba(56,120,220,0.28) 0%, transparent 55%), linear-gradient(168deg, #06101f 0%, #0c1f3a 42%, #050a12 100%)',
        display: 'flex',
        flexDirection: 'column',
        color: '#fff',
        fontFamily: "'Inter', system-ui, sans-serif",
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <div style={{
        position: 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 'max(18px, env(safe-area-inset-top)) 20px max(18px, env(safe-area-inset-bottom))',
        maxWidth: 440,
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
        minHeight: 0,
      }}>
        <AnimatePresence mode="wait">
          {phase === 'intro' && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                justifyContent: 'center', textAlign: 'center', minHeight: 0,
              }}
            >
              <img
                src="/emoji/verif.png"
                alt=""
                draggable={false}
                style={{
                  width: 96,
                  height: 96,
                  objectFit: 'contain',
                  margin: '0 auto 22px',
                  display: 'block',
                }}
              />

              <h1 style={{
                margin: '0 0 14px', fontSize: 24, fontWeight: 900, lineHeight: 1.25,
                letterSpacing: '-0.02em',
              }}>
                Vérification d&apos;identité
              </h1>
              <p style={{
                margin: '0 0 12px', fontSize: 14.5, lineHeight: 1.65,
                color: 'rgba(226,232,240,0.9)',
              }}>
                Pour renforcer la sécurité de ton compte, une vérification selfie est requise.
                Place ton visage et ta pièce d&apos;identité à côté de ta tête, puis prends la photo.
              </p>
              <p style={{
                margin: '0 0 28px', fontSize: 13, lineHeight: 1.55,
                color: 'rgba(252,165,165,0.95)', fontWeight: 700,
              }}>
                Sans validation sous 24&nbsp;h, ton accès sera banni et ton dépôt ne sera plus comptabilisé — tu perdras ton argent.
              </p>

              <motion.button
                type="button"
                whileTap={{ scale: 0.98 }}
                onClick={goCamera}
                style={{
                  width: '100%', padding: '16px', borderRadius: 16,
                  border: '1.5px solid rgba(191,219,254,0.5)',
                  background: 'linear-gradient(135deg, rgba(96,165,250,0.9), rgba(37,99,235,0.95))',
                  color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer',
                  boxShadow: '0 10px 28px rgba(37,99,235,0.4)',
                }}
              >
                Commencer la vérification
              </motion.button>
              {error && (
                <p style={{ marginTop: 14, color: '#fca5a5', fontSize: 13, fontWeight: 600 }}>{error}</p>
              )}
            </motion.div>
          )}

          {phase === 'camera' && (
            <motion.div
              key="camera"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
              <p style={{
                margin: '0 0 12px', textAlign: 'center', fontSize: 14, fontWeight: 700,
                color: 'rgba(226,232,240,0.9)',
              }}>
                Visage + pièce d&apos;identité dans le cadre
              </p>

              <div style={{
                position: 'relative', flex: 1, minHeight: 300, borderRadius: 28, overflow: 'hidden',
                border: '2px solid rgba(147,197,253,0.45)',
                boxShadow: '0 20px 48px rgba(0,0,0,0.45)',
                background: '#0a0a0a',
              }}>
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  autoPlay
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: 'scaleX(-1)',
                    background: '#111',
                  }}
                />

                {/* Guides légers — ne masquent pas le flux */}
                <div style={{
                  pointerEvents: 'none', position: 'absolute',
                  left: '16%', top: '16%', width: '46%', height: '58%',
                  border: '2px solid rgba(255,255,255,0.55)',
                  borderRadius: '50%',
                }} />
                <div style={{
                  pointerEvents: 'none', position: 'absolute',
                  right: '9%', top: '30%', width: '26%', height: '34%',
                  border: '2px dashed rgba(186,230,253,0.7)',
                  borderRadius: 10,
                }}>
                  <span style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 10, fontWeight: 800,
                    color: 'rgba(224,242,254,0.85)', letterSpacing: '0.06em',
                  }}>
                    ID
                  </span>
                </div>

                {!camReady && !error && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', background: 'rgba(0,0,0,0.45)',
                    fontSize: 13, fontWeight: 700, color: 'rgba(226,232,240,0.85)',
                  }}>
                    Activation de la caméra…
                  </div>
                )}

                <AnimatePresence>
                  {flash && (
                    <motion.div
                      key="flash"
                      initial={{ opacity: 0.85 }}
                      animate={{ opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.22 }}
                      style={{ position: 'absolute', inset: 0, background: '#fff', pointerEvents: 'none' }}
                    />
                  )}
                </AnimatePresence>
              </div>

              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '20px 0 6px', gap: 10,
              }}>
                <motion.button
                  type="button"
                  aria-label="Prendre la photo"
                  onClick={capture}
                  disabled={!camReady}
                  animate={{ scale: shutterPressed ? 0.9 : 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 28 }}
                  style={{
                    width: 78, height: 78, borderRadius: '50%',
                    padding: 0, cursor: camReady ? 'pointer' : 'wait',
                    opacity: camReady ? 1 : 0.5,
                    border: '4px solid rgba(255,255,255,0.95)',
                    background: 'radial-gradient(circle at 40% 32%, #ffffff 0%, #e8eef7 55%, #cbd5e1 100%)',
                    boxShadow: '0 10px 32px rgba(0,0,0,0.45)',
                  }}
                />
                {error ? (
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ color: '#fca5a5', fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>{error}</p>
                    <button
                      type="button"
                      onClick={() => {
                        stopCamera();
                        setError('');
                        setCamNonce((n) => n + 1);
                      }}
                      style={{
                        background: 'transparent', border: '1px solid rgba(147,197,253,0.4)',
                        color: '#93c5fd', borderRadius: 10, padding: '8px 14px',
                        fontWeight: 700, fontSize: 12, cursor: 'pointer',
                      }}
                    >
                      Réessayer la caméra
                    </button>
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: 'rgba(191,219,254,0.65)' }}>
                    Appuie pour capturer
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {(phase === 'review' || phase === 'verifying') && (
            <motion.div
              key="review"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                justifyContent: 'center', minHeight: 0,
              }}
            >
              {phase === 'verifying' ? (
                <div style={{ textAlign: 'center' }}>
                  <motion.div
                    animate={{ scale: [1, 1.06, 1] }}
                    transition={{ repeat: Infinity, duration: 1.2 }}
                    style={{
                      width: 88, height: 88, borderRadius: 24, margin: '0 auto 18px',
                      background: 'rgba(34,197,94,0.18)', border: '1.5px solid rgba(74,222,128,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 36, fontWeight: 800, color: '#86efac',
                    }}
                  >
                    ✓
                  </motion.div>
                  <p style={{ fontSize: 18, fontWeight: 900, margin: '0 0 8px' }}>Selfie envoyé</p>
                  <p style={{ fontSize: 13, color: 'rgba(191,219,254,0.7)', margin: 0, fontWeight: 600 }}>
                    Tu peux ouvrir le chat Access quand tu veux
                  </p>
                </div>
              ) : (
                <>
                  <p style={{
                    textAlign: 'center', fontSize: 17, fontWeight: 900, margin: '0 0 16px',
                  }}>
                    Confirmer cette photo ?
                  </p>
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt="Aperçu"
                      style={{
                        width: '100%', maxHeight: '46vh', objectFit: 'cover',
                        borderRadius: 22, marginBottom: 18, display: 'block',
                      }}
                    />
                  )}
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.98 }}
                    onClick={confirm}
                    style={{
                      width: '100%', padding: '16px', borderRadius: 16, marginBottom: 10,
                      border: '1.5px solid rgba(134,239,172,0.5)',
                      background: 'linear-gradient(135deg, rgba(34,197,94,0.8), rgba(21,128,61,0.9))',
                      color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer',
                    }}
                  >
                    Confirmer
                  </motion.button>
                  <button
                    type="button"
                    onClick={() => {
                      setPreviewUrl((prev) => {
                        if (prev) URL.revokeObjectURL(prev);
                        return null;
                      });
                      setBlobFile(null);
                      setPhase('camera');
                    }}
                    style={{
                      width: '100%', padding: '13px', borderRadius: 14,
                      border: '1px solid rgba(147,197,253,0.28)',
                      background: 'rgba(255,255,255,0.05)', color: 'rgba(226,232,240,0.88)',
                      fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    Reprendre
                  </button>
                  {error && (
                    <p style={{ marginTop: 12, color: '#fca5a5', fontSize: 13, textAlign: 'center', fontWeight: 600 }}>
                      {error}
                    </p>
                  )}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>,
    document.body
  );
};

export default AccessSelfieModal;
